"use client"

import {useOptionalCompanyId} from "@/hooks/use-company-id";
import {isCompanyFeatureFlagEnabled, PAID_OR_TRIAL_ORG_FEATURE_FLAG_VALUE} from "@/hooks/queries/feature-flags-utils";
import {useBoundStore} from "@/hooks/queries/stores/use-bound-store";
import {useCompanySettings} from "@/hooks/queries/use-company";

export interface SpecsEnabledState {
  specsEnabled: boolean
  loading: boolean
}

export function useSpecsEnabled(companyIdOverride?: number): boolean {
  return useSpecsEnabledState(companyIdOverride).specsEnabled;
}

export function useSpecsEnabledState(companyIdOverride?: number): SpecsEnabledState {
  const optionalCompanyId = useOptionalCompanyId();
  const companyId = companyIdOverride ?? optionalCompanyId;
  const featureFlags = useBoundStore(s => s.featureFlags);
  const ownLimitsLoaded = useBoundStore(s => s.ownLimits !== undefined);
  const companyLimits = useBoundStore(s => companyId === undefined ? undefined : s.ownCompanyLimits[companyId]?.limits);
  const {settings, error: settingsError, loading: settingsLoading} = useCompanySettings(companyId);

  if (companyId === undefined) {
    return {specsEnabled: true, loading: false};
  }
  if (featureFlags === undefined) {
    return {specsEnabled: false, loading: true};
  }
  if (featureFlags.respectDisabledSpecs.includes(PAID_OR_TRIAL_ORG_FEATURE_FLAG_VALUE) && !ownLimitsLoaded) {
    return {specsEnabled: false, loading: true};
  }

  const paidOrTrialOrgEligible = Boolean(companyLimits?.paid && !companyLimits.forcedReadonly);
  const shouldRespectDisabledSpecs = isCompanyFeatureFlagEnabled(
    featureFlags,
    companyId,
    ff => ff.respectDisabledSpecs,
    paidOrTrialOrgEligible,
  );
  if (!shouldRespectDisabledSpecs) {
    return {specsEnabled: true, loading: false};
  }

  if (settingsLoading) {
    return {specsEnabled: false, loading: true};
  }
  if (settingsError || settings === undefined) {
    return {specsEnabled: false, loading: false};
  }

  return {specsEnabled: Boolean(settings.specsEnabled), loading: false};
}
